import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  doGetIssuesForOrder,
  doCreateIssue,
  doUpdateIssue,
  doCreateComment,
  doGetIssues,
  doGetIssue,
  doMarkAsSeen,
  doMarkAllAsSeen,
  doGetUnseen,
  doCreateMissingComment,
} from './thunk.ts';

const isActionWithPending = (action) => {
  return action.type && action.type.startsWith('issues/') && action.type.endsWith('/pending');
};

const isActionWithFulfilled = (action) => {
  return action.type && action.type.startsWith('issues/') && action.type.endsWith('/fulfilled');
};

const isActionWithFailed = (action) => {
  return action.type && action.type.startsWith('issues/') && action.type.endsWith('/rejected');
};

const issuesAdapter = createEntityAdapter({
  selectId: (item) => item.id,
});

// const initialState = issuesAdapter.getInitialState({
//   action: null,
//   isFetching: false,
//   status: 'idle',
//   currentOrderId: null,
// });

const initialState = issuesAdapter.getInitialState({
  activeIssues: {
    ids: [],
    entities: {},
    unseen: 0,
    pagination: {
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      totalPages: 0,
    },
  },
  resolvedIssues: {
    ids: [],
    entities: {},
    unseen: 0,
    pagination: {
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      totalPages: 0,
    },
  },
  orderIssues: {
    active: {
      ids: [],
      entities: {},
      pagination: {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
      },
    },
    resolved: {
      ids: [],
      entities: {},
      pagination: {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
      },
    },
  },
  status: 'idle',
  isFetching: false,
  issueView: null,
});

const issuesSlice = createSlice({
  name: 'issues',
  initialState: initialState,
  reducers: {
    updateActiveIssuesPagination: (state, action) => {
      state.activeIssues.pagination = {
        ...state.activeIssues.pagination,
        ...action.payload,
      };
    },
    updateResolvedIssuesPagination: (state, action) => {
      state.resolvedIssues.pagination = {
        ...state.resolvedIssues.pagination,
        ...action.payload,
      };
    },
    updateOrderActiveIssuesPagination: (state, action) => {
      state.orderIssues.active.pagination = {
        ...state.orderIssues.active.pagination,
        ...action.payload,
      };
    },
    updateOrderResolvedIssuesPagination: (state, action) => {
      state.orderIssues.resolved.pagination = {
        ...state.orderIssues.resolved.pagination,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(doGetIssues.pending, (state) => {
      state.action = 'getall';
      state.status = 'loading';
    });
    builder.addCase(doGetIssues.fulfilled, (state, action, ...args) => {
      const { issues, pagination, unseenActive, unseenResolved } = action.payload;

      const status = action.meta.arg.status;

      state.activeIssues.unseen = unseenActive;
      state.resolvedIssues.unseen = unseenResolved;

      if (status === 'active') {
        // state.activeIssues.ids = issues.map((issue) => issue.id);
        //state.activeIssues.ids = state.activeIssues.ids.filter((id) => !state.resolvedIssues.entities[id]);

        state.activeIssues.ids = [...new Set([...state.activeIssues.ids, ...issues.map((issue) => issue.id)])];
        state.activeIssues.entities = {
          ...(action.meta.arg.page > 1 ? state.activeIssues.entities : []),
          ...issues.reduce((acc, issue) => {
            acc[issue.id] = issue;
            return acc;
          }, {}),
        };
        state.activeIssues.pagination = {
          currentPage: Math.ceil(state.activeIssues.ids.length / state.activeIssues.pagination.pageSize),
          pageSize: state.activeIssues.pagination.pageSize || 10,
          totalItems: pagination.totalItems,
          totalPages: pagination.totalPages,
        };
      } else if (status === 'resolved') {
        //state.resolvedIssues.ids = state.resolvedIssues.ids.filter((id) => !state.activeIssues.entities[id]);
        state.resolvedIssues.ids = [...new Set([...state.resolvedIssues.ids, ...issues.map((issue) => issue.id)])];
        state.resolvedIssues.entities = {
          ...state.resolvedIssues.entities,
          ...issues.reduce((acc, issue) => {
            acc[issue.id] = issue;
            return acc;
          }, {}),
        };
        state.resolvedIssues.pagination = {
          currentPage: Math.ceil(state.resolvedIssues.ids.length / state.resolvedIssues.pagination.pageSize),
          pageSize: state.resolvedIssues.pagination.pageSize || 10,
          totalItems: pagination.totalItems,
          totalPages: pagination.totalPages,
        };
      }

      state.status = 'succeeded';
      // issuesAdapter.setAll(state, issues);
    });
    builder.addCase(doGetIssues.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doGetIssuesForOrder.pending, (state, action) => {
      state.action = 'getForOrder';
      state.status = 'loading';
      state.currentOrderId = action.meta.arg;
    });
    builder.addCase(doGetIssuesForOrder.fulfilled, (state, action) => {
      const { issues, pagination } = action.payload;

      const status = action.meta.arg.status;

      if (status === 'active') {
        state.orderIssues.active.ids = issues.map((issue) => issue.id);
        state.orderIssues.active.entities = issues.reduce((acc, issue) => {
          acc[issue.id] = issue;
          return acc;
        }, {});
        state.orderIssues.active.pagination = {
          currentPage: pagination.currentPage || 1,
          pageSize: pagination.pageSize || 10,
          totalItems: pagination.totalItems,
          totalPages: pagination.totalPages,
        };
      } else if (status === 'resolved') {
        state.orderIssues.resolved.ids = issues.map((issue) => issue.id);
        state.orderIssues.resolved.entities = issues.reduce((acc, issue) => {
          acc[issue.id] = issue;
          return acc;
        }, {});
        state.orderIssues.resolved.pagination = {
          currentPage: pagination.currentPage || 1,
          pageSize: pagination.pageSize || 10,
          totalItems: pagination.totalItems,
          totalPages: pagination.totalPages,
        };
      }

      state.status = 'succeeded';
    });
    builder.addCase(doGetIssuesForOrder.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doCreateIssue.fulfilled, (state, action) => {
      issuesAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(doCreateComment.fulfilled, (state, action) => {
      issuesAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(doCreateMissingComment.fulfilled, (state, action) => {
      issuesAdapter.upsertOne(state, action.payload);
    });

    // builder.addCase(doUpdateIssue.fulfilled, (state, action) => {
    //   issuesAdapter.upsertOne(state, action.payload);
    // });
    builder.addCase(doUpdateIssue.fulfilled, (state, action) => {
      const updatedIssue = action.payload;
      const id = updatedIssue.id;

      // Remove from old category
      if (state.activeIssues.entities[id]) {
        delete state.activeIssues.entities[id];
        state.activeIssues.ids = state.activeIssues.ids.filter((issueId) => issueId !== id);
      }
      if (state.resolvedIssues.entities[id]) {
        delete state.resolvedIssues.entities[id];
        state.resolvedIssues.ids = state.resolvedIssues.ids.filter((issueId) => issueId !== id);
      }

      // Add to new category
      if (updatedIssue.status === 'RESOLVED') {
        state.resolvedIssues.ids.push(id);
        state.resolvedIssues.entities[id] = updatedIssue;
      } else {
        state.activeIssues.ids.push(id);
        state.activeIssues.entities[id] = updatedIssue;
      }
    });
    builder.addCase(doGetIssue.pending, (state) => {
      state.action = 'getOne';
      state.status = 'loading';
    });
    builder.addCase(doGetIssue.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.issueView = action.payload;
      const id = action.meta.arg.id;
      if (state.activeIssues.entities[id]) {
        state.activeIssues.entities[id].hasSeen = true;
        state.activeIssues.unseen -= 1;
      }
      if (state.resolvedIssues.entities[id]) {
        state.resolvedIssues.entities[id].hasSeen = true;
        state.resolvedIssues.unseen -= 1;
      }
      if (state.orderIssues.active.entities[id]) {
        state.orderIssues.active.entities[id].hasSeen = true;
      }
      if (state.orderIssues.resolved.entities[id]) {
        state.orderIssues.resolved.entities[id].hasSeen = true;
      }
    });
    builder.addCase(doGetIssue.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doMarkAsSeen.fulfilled, (state, action) => {
      const { issueId: id } = action.meta.arg;

      if (state.activeIssues.entities[id]) {
        state.activeIssues.entities[id].hasSeen = true;
        state.activeIssues.unseen -= 1;
      }
      if (state.resolvedIssues.entities[id]) {
        state.resolvedIssues.entities[id].hasSeen = true;
        state.resolvedIssues.unseen -= 1;
      }
      if (state.orderIssues.active.entities[id]) {
        state.orderIssues.active.entities[id].hasSeen = true;
      }
      if (state.orderIssues.resolved.entities[id]) {
        state.orderIssues.resolved.entities[id].hasSeen = true;
      }
    });
    builder.addCase(doMarkAllAsSeen.fulfilled, (state, action) => {
      const status = action.meta.arg.status;

      if (!status || status === 'active') {
        state.activeIssues.unseen = 0;

        state.activeIssues.ids.forEach((id) => {
          if (state.activeIssues.entities[id]) {
            state.activeIssues.entities[id].hasSeen = true;
          }
        });
        state.orderIssues.active.ids.forEach((id) => {
          if (state.orderIssues.active.entities[id]) {
            state.orderIssues.active.entities[id].hasSeen = true;
          }
        });
      }

      if (!status || status === 'resolved') {
        state.resolvedIssues.unseen = 0;

        state.resolvedIssues.ids.forEach((id) => {
          if (state.resolvedIssues.entities[id]) {
            state.resolvedIssues.entities[id].hasSeen = true;
          }
        });

        state.orderIssues.resolved.ids.forEach((id) => {
          if (state.orderIssues.resolved.entities[id]) {
            state.orderIssues.resolved.entities[id].hasSeen = true;
          }
        });
      }
    });
    builder.addCase(doGetUnseen.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.activeIssues.unseen = action.payload.unseenActive;
      state.resolvedIssues.unseen = action.payload.unseenResolved;
    });
    builder.addCase(doGetUnseen.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addMatcher(isActionWithPending, (state) => {
      state.isFetching = true;
      state.message = null;
    });
    builder.addMatcher(isActionWithFulfilled, (state) => {
      state.isFetching = false;
    });
    builder.addMatcher(isActionWithFailed, (state) => {
      state.isFetching = false;
      state.message = null;
    });
  },
});

// Selectors
export const { selectAll: selectAllIssues } = issuesAdapter.getSelectors((state) => state.issues);

export const selectActiveIssues = createSelector(
  (state) => state.issues.activeIssues,
  (activeIssues) => activeIssues.ids.map((id) => activeIssues.entities[id]), // Output memoized transformation
);

export const selectResolvedIssues = createSelector(
  (state) => state.issues.resolvedIssues,
  (resolvedIssues) => resolvedIssues.ids.map((id) => resolvedIssues.entities[id]), // Output memoized transformation
);

// export const selectResolvedIssues = (state) =>
//   state.issues.resolvedIssues.ids.map((id) => state.issues.resolvedIssues.entities[id]);

export const selectActiveIssuesPagination = (state) => state.issues.activeIssues.pagination;
export const selectResolvedIssuesPagination = (state) => state.issues.resolvedIssues.pagination;

export const selectActiveUnseenCount = (state) => state.issues.activeIssues.unseen;
export const selectResolvedUnseenCount = (state) => state.issues.resolvedIssues.unseen;

export const {
  updateActiveIssuesPagination,
  updateResolvedIssuesPagination,
  updateOrderActiveIssuesPagination,
  updateOrderResolvedIssuesPagination,
} = issuesSlice.actions;

export const selectOrderActiveIssues = (state) =>
  state.issues.orderIssues.active.ids.map((id) => state.issues.orderIssues.active.entities[id]);

export const selectOrderResolvedIssues = (state) =>
  state.issues.orderIssues.resolved.ids.map((id) => state.issues.orderIssues.resolved.entities[id]);

export const selectOrderActiveIssuesPagination = (state) => state.issues.orderIssues.active.pagination;

export const selectOrderResolvedIssuesPagination = (state) => state.issues.orderIssues.resolved.pagination;

export const selectIssueById = (state, id) => {
  // Check if the issue matches the `issueView`
  if (state.issues.issueView && state.issues.issueView.issueId === id) {
    return state.issues.issueView;
  }
};

// Selector for current order's issues
// export const selectCurrentOrderIssues = (state, orderId) => {
//   return selectAllIssues(state).filter((issue) => issue.orderId === orderId);
// };

const { reducer: issuesReducer } = issuesSlice;
export default issuesReducer;
